import styled, { css } from 'styled-components';
import { blockDrop } from '@shared/theme/animations';

interface BrandProps {
  $size?: 'sm' | 'md' | 'lg' | 'xl';
  $animate?: boolean;
}

const LETTERS = ['О', 'С', 'Н', 'О', 'В', 'А'] as const;
const SUFFIX = 'САТКОМ';

const sizeMap = {
  sm: { font: 14, letter: 1 },
  md: { font: 20, letter: 2 },
  lg: { font: 32, letter: 3 },
  xl: { font: 38, letter: 2 },
};

const Root = styled.span<BrandProps>`
  display: inline-flex;
  /* center выравнивает SUFFIX по вертикальной середине крупных букв ОСНОВА,
     иначе при baseline маленький суффикс «провисает» вниз. */
  align-items: center;
  font-family: ${({ theme }) => theme.font.family};
  font-weight: 800;
  letter-spacing: ${({ $size = 'md' }) => `${sizeMap[$size].letter}px`};
  font-size: ${({ $size = 'md' }) => `${sizeMap[$size].font}px`};
  color: ${({ theme }) => theme.colors.primary};
  user-select: none;
`;

const Letter = styled.span<{ $i: number; $animate: boolean }>`
  display: inline-block;
  transform-origin: bottom center;
  ${({ $animate, $i }) =>
    $animate &&
    css`
      animation: ${blockDrop} 520ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
      animation-delay: ${$i * 70}ms;
    `}
`;

const Separator = styled.span<{ $animate: boolean }>`
  display: inline-block;
  margin: 0 0.25em;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.muted};
  /* Корректировка вверх не нужна — flex align-items: center уже центрирует. */
  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${blockDrop} 520ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
      animation-delay: ${LETTERS.length * 70}ms;
    `}
`;

const Suffix = styled.span<{ $animate: boolean }>`
  display: inline-block;
  font-size: 0.62em;
  font-weight: 600;
  letter-spacing: 0.12em;
  color: ${({ theme }) => theme.colors.text.secondary};
  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${blockDrop} 520ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
      animation-delay: ${(LETTERS.length + 1) * 70}ms;
    `}
`;

/**
 * «ОСНОВА · САТКОМ» wordmark with the signature block-drop reveal.
 * Letters of ОСНОВА fall into place one-by-one — like blocks of a foundation.
 */
export function Brand({ $size = 'md', $animate = true }: BrandProps) {
  return (
    <Root $size={$size}>
      {LETTERS.map((l, i) => (
        <Letter key={i} $i={i} $animate={$animate}>
          {l}
        </Letter>
      ))}
      <Separator $animate={$animate} aria-hidden>
        ·
      </Separator>
      <Suffix $animate={$animate}>{SUFFIX}</Suffix>
    </Root>
  );
}
